import classNames from 'classnames';
import type { ReactNode } from 'react';

import { FeaturedIcon } from '../Modal/FeaturedIcon';
import type { FeaturedIconVariant } from '../Modal/FeaturedIcon';
import { Typography } from '../Typography';

import styles from './TableEmptyState.module.css';

export interface TableEmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  /** Featured-icon glyph shown in the ringed badge. */
  icon?: ReactNode;
  variant?: FeaturedIconVariant;
  /** Fully custom visual replacing the ringed featured icon. */
  illustration?: ReactNode;
  /** Action buttons (e.g. Clear search / New project). */
  actions?: ReactNode;
  className?: string;
}

/**
 * Centered empty / error placeholder for a `Table` body: a featured icon framed by concentric
 * rings, a title, supporting text and optional actions. Pass `illustration` for a fully custom
 * visual. Decorative rings are `aria-hidden`.
 */
export function TableEmptyState({
  title,
  description,
  icon,
  variant = 'gray',
  illustration,
  actions,
  className,
}: TableEmptyStateProps) {
  return (
    <div className={classNames(styles.root, className)}>
      {illustration ?? (
        <span className={styles.ripple}>
          <span className={styles.ring} aria-hidden="true" />
          <span className={styles.ring} aria-hidden="true" />
          <span className={styles.ring} aria-hidden="true" />
          <FeaturedIcon variant={variant} {...(icon != null ? { icon } : {})} />
        </span>
      )}
      <div className={styles.text}>
        <Typography
          as="h3"
          variant="text-md"
          weight="semibold"
          className={classNames(styles.title)}
        >
          {title}
        </Typography>
        {description != null && (
          <Typography as="p" variant="text-sm" className={classNames(styles.description)}>
            {description}
          </Typography>
        )}
      </div>
      {actions != null && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}
